import type { G, Svg } from "@svgdotjs/svg.js";
import type { AreaPieces, DominoTileRef, PiecesAreaLabeledPiece } from "../schemas/schema.js";
import { usePieceAt } from "./plotting.js";

/** Matches default renderer piece scale on `square*` boards. */
export const DOMINO_HAND_PIECE_SCALE = 0.85;

/** Legend composites are authored for a 500-unit cell (see `usePieceAt`). */
const LEGEND_CELL_SIZE = 500;

/** Default legend `<svg>` height for circular tokens (500×√2 user units). */
const DEFAULT_LEGEND_SYMBOL_HEIGHT = LEGEND_CELL_SIZE * Math.SQRT2;

/**
 * Half of the rendered `<use>` height for hand pieces at `scalingFactor` 1, matching `usePieceAt`.
 */
export const piecesAreaPieceVisualHalf = (
    ordinaryCellsize: number,
    scalingFactor = 1,
): number => {
    return (ordinaryCellsize / LEGEND_CELL_SIZE) * scalingFactor * DEFAULT_LEGEND_SYMBOL_HEIGHT / 2;
};

/** Vertical band for optional entry caption text, as a fraction of board cell size. */
export const PIECES_AREA_CAPTION_BAND = 0.25;

/** Gap between piece body and entry caption ink, as a fraction of board cell size. */
export const PIECES_AREA_CAPTION_GAP = 0.04;

export type PiecesAreaEntry = AreaPieces["pieces"][number];

export const isDominoTileRef = (entry: PiecesAreaEntry): entry is DominoTileRef => {
    return typeof entry === "object" && entry !== null && "domino" in entry;
};

export const isPiecesAreaLabeledPiece = (entry: PiecesAreaEntry): entry is PiecesAreaLabeledPiece => {
    return typeof entry === "object" && entry !== null && "piece" in entry;
};

export const piecesAreaLegendKey = (entry: PiecesAreaEntry): string => {
    if (typeof entry === "string") {
        return entry;
    }
    if (isPiecesAreaLabeledPiece(entry)) {
        return entry.piece;
    }
    throw new Error("piecesAreaLegendKey does not apply to domino tile refs.");
};

export const piecesAreaCaption = (
    entry: PiecesAreaEntry,
): { text?: string; textPosition: "above" | "below" } => {
    if (typeof entry === "string") {
        return { textPosition: "below" };
    }
    const textPosition = entry.textPosition ?? "below";
    if (entry.text === undefined) {
        return { textPosition };
    }
    return { text: entry.text, textPosition };
};

export const piecesAreaCaptionBandSize = (boardCellsize: number): number => {
    return boardCellsize * PIECES_AREA_CAPTION_BAND;
};

export const piecesAreaCaptionGapSize = (boardCellsize: number): number => {
    return boardCellsize * PIECES_AREA_CAPTION_GAP;
};

export const piecesAreaBodyHeight = (
    entry: PiecesAreaEntry,
    ordinaryCellsize: number,
    boardCellsize: number,
): number => {
    return isDominoTileRef(entry) ? boardCellsize : ordinaryCellsize;
};

/** Whether hand pieces in a `pieces` area should receive board rotation at placement time. */
export const shouldRotateAreaPieces = (area: AreaPieces): boolean => {
    if (area.rotateWithBoard !== undefined) {
        return area.rotateWithBoard;
    }
    return !area.pieces.every(isDominoTileRef);
};

export const piecesAreaSlotWidth = (entry: PiecesAreaEntry, ordinaryCellsize: number, boardCellsize: number): number => {
    return isDominoTileRef(entry) ? boardCellsize * 2 : ordinaryCellsize;
};

export const piecesAreaSlotHeight = (entry: PiecesAreaEntry, ordinaryCellsize: number, boardCellsize: number): number => {
    if (isDominoTileRef(entry)) {
        const { text } = piecesAreaCaption(entry);
        if (text === undefined) {
            return boardCellsize;
        }
        return boardCellsize + piecesAreaCaptionBandSize(boardCellsize);
    }
    const visualHeight = piecesAreaPieceVisualHalf(ordinaryCellsize) * 2;
    const { text, textPosition } = piecesAreaCaption(entry);
    if (text === undefined) {
        return visualHeight;
    }
    const band = piecesAreaCaptionBandSize(boardCellsize);
    const gap = piecesAreaCaptionGapSize(boardCellsize);
    if (textPosition === "above") {
        return band + visualHeight;
    }
    return visualHeight + gap + band;
};

/** Y coordinate of the piece (or domino tile) vertical centre within a slot. */
export const piecesAreaPieceCenterYFromSlotTop = (
    slotTop: number,
    entry: PiecesAreaEntry,
    ordinaryCellsize: number,
    boardCellsize: number,
): number => {
    if (isDominoTileRef(entry)) {
        const { text, textPosition } = piecesAreaCaption(entry);
        if (text === undefined) {
            return slotTop + boardCellsize / 2;
        }
        const band = piecesAreaCaptionBandSize(boardCellsize);
        if (textPosition === "above") {
            return slotTop + band + boardCellsize / 2;
        }
        return slotTop + boardCellsize / 2;
    }
    const half = piecesAreaPieceVisualHalf(ordinaryCellsize);
    const { text, textPosition } = piecesAreaCaption(entry);
    if (text === undefined) {
        return slotTop + half;
    }
    const band = piecesAreaCaptionBandSize(boardCellsize);
    if (textPosition === "above") {
        return slotTop + band + half;
    }
    return slotTop + half;
};

/** Y coordinate for caption text centre (legacy layout midpoint), or undefined when there is no caption. */
export const piecesAreaCaptionCenterYFromSlotTop = (
    slotTop: number,
    entry: PiecesAreaEntry,
    ordinaryCellsize: number,
    boardCellsize: number,
): number | undefined => {
    const { text, textPosition } = piecesAreaCaption(entry);
    if (text === undefined) {
        return undefined;
    }
    const half = isDominoTileRef(entry)
        ? boardCellsize / 2
        : piecesAreaPieceVisualHalf(ordinaryCellsize);
    const band = piecesAreaCaptionBandSize(boardCellsize);
    const gap = piecesAreaCaptionGapSize(boardCellsize);
    if (textPosition === "above") {
        return slotTop + band / 2;
    }
    const visualBottom = slotTop + half * 2;
    return visualBottom + gap + band / 2;
};

/**
 * Ink-top Y for entry captions (see `placeTextInkTop`), or undefined when there is no caption.
 */
export const piecesAreaCaptionTextYFromSlotTop = (
    slotTop: number,
    entry: PiecesAreaEntry,
    ordinaryCellsize: number,
    boardCellsize: number,
): number | undefined => {
    const { text, textPosition } = piecesAreaCaption(entry);
    if (text === undefined) {
        return undefined;
    }
    const gap = piecesAreaCaptionGapSize(boardCellsize);
    if (textPosition === "above") {
        return slotTop + gap;
    }
    const half = isDominoTileRef(entry)
        ? boardCellsize / 2
        : piecesAreaPieceVisualHalf(ordinaryCellsize);
    const centerY = piecesAreaPieceCenterYFromSlotTop(slotTop, entry, ordinaryCellsize, boardCellsize);
    return centerY + half + gap;
};

/** Top edge Y for a domino tile group within a slot. */
export const piecesAreaDominoTileTopFromSlotTop = (
    slotTop: number,
    entry: PiecesAreaEntry,
    boardCellsize: number,
): number => {
    const { text, textPosition } = piecesAreaCaption(entry);
    if (text === undefined || textPosition !== "above") {
        return slotTop;
    }
    return slotTop + piecesAreaCaptionBandSize(boardCellsize);
};

/** Board cells consumed by one entry when wrapping a `pieces` area row. */
export const piecesAreaCellSpan = (entry: PiecesAreaEntry): number => {
    return isDominoTileRef(entry) ? 2 : 1;
};

/**
 * Pack area entries into rows by board cell width. Domino tiles count as two cells
 * and are never split across rows.
 */
export const buildPiecesAreaRows = (
    pieces: PiecesAreaEntry[],
    maxCellsPerRow: number,
): number[][] => {
    const rows: number[][] = [];
    let currentRow: number[] = [];
    let cellsInRow = 0;
    for (let i = 0; i < pieces.length; i++) {
        const span = piecesAreaCellSpan(pieces[i]);
        if (cellsInRow > 0 && cellsInRow + span > maxCellsPerRow) {
            rows.push(currentRow);
            currentRow = [];
            cellsInRow = 0;
        }
        currentRow.push(i);
        cellsInRow += span;
    }
    if (currentRow.length > 0) {
        rows.push(currentRow);
    }
    return rows;
};

/** Extra horizontal space after each domino tile in a `pieces` area (fraction of board cell size). */
export const DOMINO_HAND_TILE_GAP = 0.25;

/** Vertical space between wrapped rows in a `pieces` area (fraction of board cell size). */
export const PIECES_AREA_ROW_GAP = 0.10;

export const piecesAreaHorizontalGap = (
    entry: PiecesAreaEntry,
    hpad: number,
    boardCellsize: number,
): number => {
    return hpad + (isDominoTileRef(entry) ? boardCellsize * DOMINO_HAND_TILE_GAP : 0);
};

export const piecesAreaVerticalGap = (hpad: number, boardCellsize: number): number => {
    return hpad + boardCellsize * PIECES_AREA_ROW_GAP;
};

export const dominoClickPayload = (handle: string | number, leftKey: string, rightKey: string, end: "L" | "R"): string => {
    return `_domino_${handle}_${leftKey}_${rightKey}_${end}`;
};

/**
 * Place two legend halves as adjacent board cells: same centres, scale, and
 * sheetCellSize as the default renderer on `square*` boards.
 */
export const composeDominoTile = (
    parent: Svg,
    rootSvg: Svg,
    leftKey: string,
    rightKey: string,
    tileHeight: number,
    scalingFactor: number = DOMINO_HAND_PIECE_SCALE,
): G => {
    const leftPiece = rootSvg.findOne("#" + leftKey) as Svg;
    const rightPiece = rootSvg.findOne("#" + rightKey) as Svg;
    if ( (leftPiece === null) || (leftPiece === undefined) ) {
        throw new Error(`Could not find the requested domino end (${leftKey}). Each end *must* exist in the \`legend\`.`);
    }
    if ( (rightPiece === null) || (rightPiece === undefined) ) {
        throw new Error(`Could not find the requested domino end (${rightKey}). Each end *must* exist in the \`legend\`.`);
    }
    const tileWidth = tileHeight * 2;
    const tile = parent.nested().size(tileWidth, tileHeight).viewbox(0, 0, tileWidth, tileHeight);
    const centerY = tileHeight / 2;
    usePieceAt({
        svg: tile,
        piece: leftPiece,
        cellsize: tileHeight,
        x: tileHeight / 2,
        y: centerY,
        scalingFactor,
        sheetCellSize: LEGEND_CELL_SIZE,
    });
    usePieceAt({
        svg: tile,
        piece: rightPiece,
        cellsize: tileHeight,
        x: tileHeight + (tileHeight / 2),
        y: centerY,
        scalingFactor,
        sheetCellSize: LEGEND_CELL_SIZE,
    });
    return tile;
};
